import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { any, z } from "zod";
import {
  ArrowLeft,
  CalendarDays,
  CheckSquare2,
  FileText,
  Loader2,
  PackageX,
  ReceiptText,
  User,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import {
  useGetASalesReturnEntry,
  useSaveSalesInvoice,
} from "../service/salesReturnService";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../utils/components/ui/breadcrumb";
import { InvoiceProductResponse } from "../types/salesReturn";

// ── Schema ────────────────────────────────────────────────────────────────────

export const SalesReturnProductSchema = z.object({
  id: z.number().optional().default(0),
  salesEntryProductId: z.number().optional().default(0),
  returnQuantity: z.coerce
    .number()
    .positive("Must be greater than zero")
    .optional()
    .default(1),
  reason: z.string().optional().default(""),
  unitPrice: z.number().optional().default(0),
  discount: z.number().optional(),
  tax: z.number().optional(),
});

const SalesReturnSchema = z.object({
  id: z.number().optional().default(0),
  salesEntryId: z.number().optional(),
  returnDate: z.string().optional(),
  reason: z.string().optional().default(""),
  roundingAmount: z.string().optional(),
  salesReturnProductRequests: z.array(SalesReturnProductSchema).optional(),
  attachment: z.string().optional(),
  invoiceNumber: z.string().optional(),
  customerName: z.string().optional(),
  purchaseDate: z.string().optional(),
  returnType: any(),
  paymentMode: z.string().optional(),
  onlinePaymentRefNo: z.string().optional(),
});

export type SalesReturnSchemaT = z.infer<typeof SalesReturnSchema>;

// ── Component ─────────────────────────────────────────────────────────────────

function NewSalesReturn() {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: salesReturnData } = useGetASalesReturnEntry(id || "");
  const { mutate: saveSalesInvoice, isPending } = useSaveSalesInvoice();

  // Track which product indices are selected for return
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    new Set(),
  );

  const products: InvoiceProductResponse[] =
    salesReturnData?.invoiceProductResponses || [];

  const toggleProduct = (idx: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIndices.size === products.length) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(products.map((_, i) => i)));
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    reset,
  } = useForm<SalesReturnSchemaT>({
    resolver: zodResolver(SalesReturnSchema) as any,
    defaultValues: {
      id: 0,
      returnDate: new Date().toISOString().split("T")[0],
      returnType: "",
      reason: "",
      salesReturnProductRequests: products.map((item) => ({
        id: 0,
        salesEntryProductId: item.id || 0,
        returnQuantity: 1,
        reason: "",
        unitPrice: item.unitPrice || 0,
        tax: item.rateOfTax || 0,
        discount: item.discountAmount || 0,
      })),
    },
  });

  // Reset form when data loads (async)
  useEffect(() => {
    if (salesReturnData && products.length > 0) {
      reset({
        id: 0,
        returnDate: new Date().toISOString().split("T")[0],
        returnType: "",
        reason: "",
        salesReturnProductRequests: products.map((item) => ({
          id: 0,
          salesEntryProductId: item.id || 0,
          returnQuantity: 1,
          reason: "",
          unitPrice: item.unitPrice || 0,
          tax: item.rateOfTax || 0,
          discount: item.discountAmount || 0,
        })),
      });
    }
  }, [salesReturnData]);

  const watchedRequests = watch("salesReturnProductRequests") || [];
  const paymentMode = watch("paymentMode");

  // Calculate estimated return amount per the new formula
  const calculateReturnAmount = (
    product: InvoiceProductResponse,
    returnQty: number,
  ) => {
    const baseAmount = product.unitPrice * returnQty;
    const invoiceLineTotal = product.unitPrice * product.qty;
    const discountAmountForReturn =
      invoiceLineTotal > 0
        ? baseAmount * (product.discountAmount / invoiceLineTotal)
        : 0;
    const afterDiscount = baseAmount - discountAmountForReturn;
    const taxAmount = afterDiscount * (product.rateOfTax / 100);
    return afterDiscount + taxAmount;
  };

  // Return total for selected items
  const returnTotal = Array.from(selectedIndices).reduce((sum, idx) => {
    const product = products[idx];
    const qty = Number(watchedRequests[idx]?.returnQuantity || 1);
    return sum + calculateReturnAmount(product, qty);
  }, 0);

  const onSubmit = (data: SalesReturnSchemaT) => {
    if (selectedIndices.size === 0) {
      toast.error("Please select at least one product to return.");
      return;
    }

    // Validate selected rows have a reason
    const missingReason = Array.from(selectedIndices).some(
      (idx) => !data.salesReturnProductRequests?.[idx]?.reason?.trim(),
    );
    if (missingReason) {
      toast.error("Please provide a reason for each selected product.");
      return;
    }

    const postData = {
      salesEntryId: salesReturnData?.id || 0,
      returnDate: data.returnDate || new Date().toISOString().split("T")[0],
      reason: data.reason || "",
      paymentMode: data.paymentMode || "CASH",
      onlinePaymentRefNo: data.onlinePaymentRefNo || "",
      salesReturnProductRequests: Array.from(selectedIndices).map((idx) => {
        const item = data.salesReturnProductRequests![idx];
        const original = products[idx];
        const returnQty = Number(item?.returnQuantity || 1);
        const conversion = Number(original?.conversionToBaseUnit || 1);
        return {
          salesEntryProductId: original?.id || 0,
          returnQuantity: returnQty,
          unit: original?.unit || "",
          conversionToBaseUnit: conversion,
          baseReturnQuantity: returnQty * conversion,
          reason: item?.reason || "",
        };
      }),
    };

    saveSalesInvoice(postData, {
      onSuccess: () => {
        toast.success("Sales return saved successfully!");
        navigate("/sales-return");
      },
      onError: (error: Error) => {
        toast.error(`Error saving sales return: ${error.message}`);
      },
    });
  };

  const allSelected =
    products.length > 0 && selectedIndices.size === products.length;
  const someSelected = selectedIndices.size > 0 && !allSelected;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="min-h-full py-6 px-1 space-y-6"
    >
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/">Home</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/sales-return">Sales Return</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Create Sales Return</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Page header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/sales-return")}
          className="p-2 rounded-[10px] border border-[#D7E2F5] text-[#52627C] hover:bg-[#F5F8FD] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1A5EF9]">
            Create Sales Return
          </h1>
          <p className="text-sm text-[#7D8998] mt-0.5">
            Select products and enter return details
          </p>
        </div>
      </div>

      {/* Invoice / Customer info card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
        <h2 className="text-base font-semibold text-[#21314A] mb-5 flex items-center gap-2">
          <ReceiptText className="w-4 h-4 text-[#1A5EF9]" />
          Invoice Details
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Customer */}
          <div>
            <label className="text-xs font-medium text-[#7D8998] mb-1.5 flex items-center gap-1">
              <User className="w-3 h-3" /> Customer
            </label>
            <div className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 flex items-center text-sm text-[#23324D] font-medium">
              {salesReturnData?.customer?.username || "—"}
            </div>
          </div>

          {/* Invoice number */}
          <div>
            <label className="text-xs font-medium text-[#7D8998] mb-1.5 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Invoice Number
            </label>
            <div className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 flex items-center text-sm text-[#23324D] font-medium">
              {salesReturnData?.invoiceNumber || "—"}
            </div>
          </div>

          {/* Purchase date */}
          <div>
            <label className="text-xs font-medium text-[#7D8998] mb-1.5 flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> Purchase Date
            </label>
            <div className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 flex items-center text-sm text-[#23324D]">
              {salesReturnData?.salesDate || "—"}
            </div>
          </div>

          {/* Return date */}
          <div>
            <label className="text-xs font-medium text-[#7D8998] mb-1.5 flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> Return Date
            </label>
            <input
              type="date"
              {...register("returnDate")}
              className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
            />
          </div>
        </div>

        {/* Payment mode row */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-medium text-[#7D8998] mb-1.5 block">
              Payment Mode <span className="text-red-400">*</span>
            </label>
            <Controller
              name="paymentMode"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                >
                  <option value="">Select mode</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="CREDIT">Credit</option>
                  <option value="ONLINE_PAYMENT">Online</option>
                  <option value="MIXED">Mixed</option>
                </select>
              )}
            />
          </div>

          {paymentMode === "ONLINE_PAYMENT" && (
            <div>
              <label className="text-xs font-medium text-[#7D8998] mb-1.5 block">
                Online Ref No
              </label>
              <input
                type="text"
                {...register("onlinePaymentRefNo")}
                placeholder="Reference number"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
          )}
        </div>
      </div>

      {/* Products selection card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
        {/* Card header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2FA] bg-[#F8FAFF]">
          <div className="flex items-center gap-2">
            <PackageX className="w-4 h-4 text-[#1A5EF9]" />
            <h2 className="text-base font-semibold text-[#21314A]">
              Select Products to Return
            </h2>
            {selectedIndices.size > 0 && (
              <span className="inline-flex items-center rounded-full bg-[#E8EFFF] text-[#1A5EF9] text-xs font-semibold px-2.5 py-0.5">
                {selectedIndices.size} selected
              </span>
            )}
          </div>
          {products.length > 0 && (
            <button
              type="button"
              onClick={selectAll}
              className="text-xs font-medium text-[#1A5EF9] hover:underline"
            >
              {allSelected ? "Deselect all" : "Select all"}
            </button>
          )}
        </div>

        {products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[#8D98A7]">
            <PackageX className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">No products available for return</p>
          </div>
        ) : (
          <div className="divide-y divide-[#EEF2FA]">
            {products.map((product, idx) => {
              const isSelected = selectedIndices.has(idx);
              const watchedQty = Number(
                watchedRequests[idx]?.returnQuantity || 1,
              );
              const lineTotal = calculateReturnAmount(product, watchedQty);

              return (
                <div
                  key={product.id}
                  className={`transition-colors ${
                    isSelected
                      ? "bg-[#F5F8FF] border-l-4 border-l-[#1A5EF9]"
                      : "bg-white border-l-4 border-l-transparent hover:bg-[#FAFCFF]"
                  }`}
                >
                  {/* Product row */}
                  <div className="px-6 py-4">
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <div className="pt-0.5">
                        <input
                          type="checkbox"
                          id={`select-product-${idx}`}
                          checked={isSelected}
                          onChange={() => toggleProduct(idx)}
                          className="w-4 h-4 rounded accent-[#1A5EF9] cursor-pointer"
                        />
                      </div>

                      {/* Product info */}
                      <label
                        htmlFor={`select-product-${idx}`}
                        className="flex-1 cursor-pointer"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          {/* Left: name + badges */}
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm font-semibold truncate ${
                                isSelected ? "text-[#1A5EF9]" : "text-[#21314A]"
                              }`}
                            >
                              {product.product?.productName || "—"}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-1.5">
                              <span className="inline-flex items-center rounded-full bg-[#F0F4FF] text-[#4B6EF5] text-xs px-2 py-0.5">
                                Unit:{" "}
                                {product.unit || product.product?.unit || "—"}
                              </span>
                              <span className="inline-flex items-center rounded-full bg-[#F0FAF4] text-[#0E9F6E] text-xs px-2 py-0.5">
                                Purchased: {product.qty}
                              </span>
                              <span className="inline-flex items-center rounded-full bg-[#FFF7ED] text-[#D97706] text-xs px-2 py-0.5">
                                Available: {product.availableQty ?? "—"}
                                {product.unit ? ` ${product.unit}` : ""}
                              </span>
                              {product.rateOfTax ? (
                                <span className="inline-flex items-center rounded-full bg-[#F5F5F5] text-[#6B7280] text-xs px-2 py-0.5">
                                  Tax {product.rateOfTax}%
                                </span>
                              ) : null}
                              {product.discountAmount ? (
                                <span className="inline-flex items-center rounded-full bg-[#F5F5F5] text-[#6B7280] text-xs px-2 py-0.5">
                                  Discount: {product.discountAmount.toFixed(3)}
                                  {product.discountPercentage
                                    ? ` (${product.discountPercentage}%)`
                                    : ""}
                                </span>
                              ) : null}
                            </div>
                          </div>

                          {/* Right: unit price */}
                          <div className="text-right shrink-0">
                            <p className="text-xs text-[#7D8998]">Unit Price</p>
                            <p className="text-sm font-bold text-[#21314A]">
                              {product.unitPrice?.toFixed(3)}
                            </p>
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* Expanded return inputs — only when selected */}
                    {isSelected && (
                      <div className="mt-4 ml-8 grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-[14px] bg-white border border-[#D8E4FB]">
                        {/* Return Quantity */}
                        <div>
                          <label className="text-xs font-medium text-[#52627C] mb-1.5 block">
                            Return Quantity{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="number"
                            min="0.0001"
                            step="any"
                            max={product.availableQty ?? undefined}
                            placeholder="e.g. 1"
                            {...register(
                              `salesReturnProductRequests.${idx}.returnQuantity`,
                              { valueAsNumber: true },
                            )}
                            className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#1A5EF9]"
                          />
                          {errors.salesReturnProductRequests?.[idx]
                            ?.returnQuantity && (
                            <p className="text-red-500 text-xs mt-1">
                              {
                                errors.salesReturnProductRequests[idx]
                                  .returnQuantity?.message
                              }
                            </p>
                          )}
                        </div>

                        {/* Reason */}
                        <div className="sm:col-span-2">
                          <label className="text-xs font-medium text-[#52627C] mb-1.5 block">
                            Reason for Return{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Damaged, Wrong item…"
                            {...register(
                              `salesReturnProductRequests.${idx}.reason`,
                            )}
                            className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#1A5EF9]"
                          />
                          {errors.salesReturnProductRequests?.[idx]?.reason && (
                            <p className="text-red-500 text-xs mt-1">
                              {
                                errors.salesReturnProductRequests[idx].reason
                                  ?.message
                              }
                            </p>
                          )}
                        </div>

                        {/* Return total preview */}
                        <div className="sm:col-span-3 flex justify-end">
                          <div className="text-right">
                            <p className="text-xs text-[#7D8998]">
                              Return Total
                            </p>
                            <p className="text-base font-bold text-[#1A5EF9]">
                              {lineTotal.toFixed(3)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary + actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Return total summary */}
        {selectedIndices.size > 0 && (
          <div className="rounded-[16px] border border-[#D8E4FB] bg-[#F0F4FF] px-5 py-3 flex items-center gap-4">
            <CheckSquare2 className="w-5 h-5 text-[#1A5EF9] shrink-0" />
            <div>
              <p className="text-xs text-[#4B6EF5]">
                {selectedIndices.size}{" "}
                {selectedIndices.size === 1 ? "product" : "products"} selected
              </p>
              <p className="text-base font-bold text-[#1A5EF9]">
                Return Total: {returnTotal.toFixed(3)}
              </p>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 ml-auto">
          <button
            type="button"
            onClick={() => navigate("/sales-return")}
            className="h-11 rounded-[12px] border border-[#D7E2F5] px-6 text-[#52627C] hover:bg-[#F5F8FD] font-medium text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending || selectedIndices.size === 0}
            className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE] font-medium text-sm flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending && <Loader2 className="animate-spin w-4 h-4" />}
            Save Return
          </button>
        </div>
      </div>
    </form>
  );
}

export default NewSalesReturn;
